import {
    GooglePlace,
    type GooglePlaceAddressComponent,
    type GooglePlaceFields,
    type GooglePlacePhoto,
    mapGooglePlaceDetailsToTags,
    mapGooglePlacePriceLevel,
    type OfferingCategory,
    type Tag,
} from "@albertine/types";
import type { CatalogItemFull } from "@albertine/types/openapi";
import { z } from "zod";
import { placeTypesToOfferingCategories } from "../../../types/GooglePlaces";
import {
    type AddressComponent,
    type AddressComponents,
    type CatalogItem,
    type CatalogItemEditables,
    safelyConvertLocationToGeoPoint,
} from "../../CatalogItem";
import type { Booking, MemberBooking } from "../../types/Booking";
import { excludeUndefined } from "../array.util";
import { encodeSearchQuery } from "../url.util";

export function googlePlaceURL(
    placeId: string,
    fields: GooglePlaceFields,
    apiKey: string,
): string {
    const fieldsToUrl = fields.join(",");
    const url = `https://places.googleapis.com/v1/places/${placeId}?fields=${fieldsToUrl}&key=${apiKey}&languageCode=en`;
    return url;
}

export const GooglePhoto = z.object({
    name: z.string(),
    photoUri: z.string(),
});

export type GooglePhoto = z.infer<typeof GooglePhoto>;

export type GooglePhotoOption = {
    maxHeightPx: number;
    maxWidthPx: number;
};

export const photoUrlOptions: GooglePhotoOption = {
    maxWidthPx: 1200,
    maxHeightPx: 1200,
};

export function googlePhotoURL(
    resourceName: string,
    options: GooglePhotoOption,
    apiKey: string,
): string {
    const parametersToUrl = encodeSearchQuery(options);
    const url = `https://places.googleapis.com/v1/${resourceName}/media?&key=${apiKey}&${parametersToUrl}&skipHttpRedirect=true`;
    return url;
}

export type PlacePrediction = {
    placeId: string;
    label: string;
};

export type GooglePlaceService = {
    fetchPlacePhoto: (
        resourceName: string,
        options: GooglePhotoOption,
    ) => Promise<GooglePhoto | undefined>;

    populatePlacePhotoUris: (
        place: GooglePlace,
        options?: GooglePhotoOption,
        limit?: number,
    ) => Promise<GooglePlace>;

    fetchGooglePlace: (
        placeId: string,
        fields: GooglePlaceFields,
    ) => Promise<GooglePlace | undefined>;

    findPlacePredictions: (
        textQuery: string,
        types: string[],
    ) => Promise<PlacePrediction[]>;

    findPlaceId: (textQuery: string) => Promise<string | undefined>;
};

export function googlePlaceService(apiKey: string): GooglePlaceService {
    return {
        async fetchPlacePhoto(
            resourceName: string,
            options: GooglePhotoOption,
        ): Promise<GooglePhoto | undefined> {
            const url = googlePhotoURL(resourceName, options, apiKey);
            const response = await fetch(url);

            if (!response.ok) {
                return undefined;
            }

            const data = await response.json();
            return GooglePhoto.parse(data);
        },

        async populatePlacePhotoUris(
            place: GooglePlace,
            options: GooglePhotoOption = photoUrlOptions,
            limit?: number,
        ): Promise<GooglePlace> {
            const photos = (place.photos ?? []).slice(0, limit).filter(Boolean);

            const populated = (
                await Promise.all(
                    photos.map(async (photo): Promise<GooglePlacePhoto | undefined> => {
                        const url = googlePhotoURL(photo.name, options, apiKey);
                        const response = await fetch(url);
                        if (!response.ok) return undefined;

                        const parsed = GooglePhoto.parse(await response.json());
                        return { ...photo, photoUri: parsed.photoUri };
                    }),
                )
            ).filter((photo): photo is GooglePlacePhoto => photo !== undefined);

            return { ...place, photos: populated };
        },

        async fetchGooglePlace(
            placeId: string,
            fields: GooglePlaceFields,
        ): Promise<GooglePlace | undefined> {
            const url = googlePlaceURL(placeId, fields, apiKey);
            const response = await fetch(url);

            if (!response.ok) {
                return undefined;
            }

            const data = await response.json();
            return GooglePlace.parse(data);
        },

        async findPlacePredictions(
            textQuery: string,
            types: string[],
        ): Promise<PlacePrediction[]> {
            if (!textQuery.trim() || !window.google?.maps?.places) {
                return [];
            }

            const autocomplete = new google.maps.places.AutocompleteService();
            return new Promise((resolve) => {
                autocomplete.getPlacePredictions(
                    { input: textQuery, types },
                    (predictions) => {
                        resolve(
                            (predictions ?? []).flatMap((prediction) =>
                                prediction.place_id
                                    ? [
                                          {
                                              placeId: prediction.place_id,
                                              label: prediction.description,
                                          },
                                      ]
                                    : [],
                            ),
                        );
                    },
                );
            });
        },

        async findPlaceId(textQuery: string): Promise<string | undefined> {
            const [first] = await this.findPlacePredictions(textQuery, [
                "establishment",
            ]);
            return first?.placeId;
        },
    };
}

export function googleAddressSearchUrl(address: string) {
    return `https://maps.google.com/?q=${encodeURIComponent(address)}`;
}

export function googlePlaceIdUrl(googlePlaceId: string) {
    return `https://www.google.com/maps/place/?q=place_id:${googlePlaceId}`;
}

function googleLatLongUrl(lat: number, lon: number) {
    return `https://maps.google.com/?ll=${lat},${lon}`;
}

export function googleViewInMapUrl(
    item:
        | Pick<Booking, "googleUrl" | "geoPoint" | "address">
        | MemberBooking
        | CatalogItem
        | CatalogItemEditables
        | undefined,
): string | undefined {
    if (!item) return undefined;
    if (item.googleUrl) return item.googleUrl;
    if (item.geoPoint?.latitude && item.geoPoint.longitude)
        return googleLatLongUrl(item.geoPoint.latitude, item.geoPoint.longitude);
    if (item.address && item.address.length !== 0)
        return googleAddressSearchUrl(item.address);
    return undefined;
}

const PlaceTypesToTags: Record<string, Tag[]> = {
    // https://developers.google.com/maps/documentation/places/web-service/place-types
    // Culture
    art_gallery: ["type:art-gallery", "type:art"],
    museum: ["type:museum"],
    performing_arts_theater: ["type:theatre"],
    // Entertainment and Recreation
    amusement_park: ["type:tourist-attraction"],
    aquarium: ["type:tourist-attraction"],
    night_club: ["type:nightclub"],
    tourist_attraction: ["type:tourist-attraction"],
    zoo: ["type:tourist-attraction"],
    // Food and Drink
    american_restaurant: ["cuisine:american", "type:restaurant"],
    bar: ["type:bar"],
    barbecue_restaurant: ["type:restaurant"],
    brazilian_restaurant: ["cuisine:latin", "type:restaurant"],
    breakfast_restaurant: ["meals:breakfast", "type:restaurant"],
    brunch_restaurant: ["meals:brunch", "type:restaurant"],
    cafe: ["type:cafe"],
    chinese_restaurant: ["cuisine:asian", "cuisine:chinese", "type:restaurant"],
    coffee_shop: ["type:cafe"],
    french_restaurant: ["cuisine:french", "type:restaurant"],
    greek_restaurant: ["cuisine:greek", "type:restaurant"],
    indian_restaurant: ["cuisine:indian", "cuisine:asian", "type:restaurant"],
    indonesia_restaurant: ["cuisine:asian", "type:restaurant"],
    italian_restaurant: ["cuisine:italian", "type:restaurant"],
    japanese_restaurant: ["cuisine:japanese", "cuisine:asian", "type:restaurant"],
    korean_restaurant: ["cuisine:asian", "type:restaurant"],
    lebanese_restaurant: ["cuisine:middle-eastern", "type:restaurant"],
    mediterranean_restaurant: ["cuisine:mediterranean", "type:restaurant"],
    mexican_restaurant: ["cuisine:mexican", "type:restaurant"],
    middle_eastern_restaurant: ["cuisine:middle-eastern", "type:restaurant"],
    ramen_restaurant: ["cuisine:asian", "type:restaurant"],
    restaurant: ["type:restaurant"],
    seafood_restaurant: ["type:restaurant", "cuisine:seafood"],
    spanish_restaurant: ["cuisine:spanish", "type:restaurant"],
    steak_house: ["cuisine:steakhouse", "type:restaurant"],
    sushi_restaurant: ["cuisine:asian", "cuisine:japanese", "type:restaurant"],
    thai_restaurant: ["cuisine:asian", "type:restaurant"],
    turkish_restaurant: ["cuisine:middle-eastern", "type:restaurant"],
    vegan_restaurant: ["goodFor:vegans", "goodFor:vegetarians", "type:restaurant"],
    vegetarian_restaurant: ["goodFor:vegetarians", "type:restaurant"],
    vietnamese_restaurant: ["cuisine:asian", "type:restaurant"],
    // Health and Wellness
    spa: ["type:spa"],
    // Lodging
    bed_and_breakfast: ["type:bed-and-breakfast"],
    hotel: ["type:hotel"],
    resort_hotel: ["type:resort", "type:hotel"],
    // Services
    beauty_salon: ["type:beauty-salon"],
    book_store: ["type:bookshop"],
};

function typesToTags(types: string[]): Tag[] {
    const tags = types.flatMap((type): Tag[] => {
        const tag = PlaceTypesToTags[type];
        return tag || [];
    });
    return tags;
}

export function mapGooglePlaceToTags(place: GooglePlace | undefined | null): Tag[] {
    if (!place) return [];
    const priceTags = mapGooglePlacePriceLevel(place);
    const typeTags = typesToTags(place.types || []);
    const detailTags = mapGooglePlaceDetailsToTags(place);
    const tags = [...priceTags, ...typeTags, ...detailTags];
    return Array.from(new Set(tags)).sort();
}

export function newCatalogItemPayloadFromGooglePlace(
    place: GooglePlace,
): Partial<CatalogItemFull & { googlePlaceData: GooglePlace }> {
    const {
        location,
        displayName,
        id: placeId,
        addressComponents,
        formattedAddress,
        internationalPhoneNumber,
        googleMapsUri,
        websiteUri: website,
        types,
    } = place;
    const geoPoint = safelyConvertLocationToGeoPoint(location);
    const offeringCategories: OfferingCategory[] = placeTypesToOfferingCategories(
        types || [],
    );
    const tags = mapGooglePlaceToTags(place);

    return {
        googlePlaceId: placeId,
        name: displayName?.text || "",
        address: formattedAddress ?? null,
        addressComponents:
            addressComponents
                ?.map(addressComponentOrUndefined)
                ?.filter(excludeUndefined) ?? null,
        website: website ? website.split("?")[0] : null,
        phoneNumber: internationalPhoneNumber ?? null,
        googleUrl: googleMapsUri ?? null,
        locationGeoPoint: geoPoint ?? null,
        offeringCategories,
        tags,
        googlePlaceData: place,
    };
}

export function googlePlaceToAddressComponents(
    place: GooglePlace,
): AddressComponents | undefined {
    return (
        place.addressComponents
            ?.map(addressComponentOrUndefined)
            .filter(excludeUndefined) ?? undefined
    );
}

export function toAddressComponents(
    components: GooglePlaceAddressComponent[] | undefined | null,
): AddressComponents | undefined {
    if (!components) return undefined;

    return components.map(addressComponentOrUndefined).filter(excludeUndefined);
}

function addressComponentOrUndefined(
    component: GooglePlaceAddressComponent,
): AddressComponent | undefined {
    if (!component) return undefined;
    if (!component.longText || !component.shortText || !component.types) {
        return undefined;
    }
    return {
        longName: component.longText,
        shortName: component.shortText,
        types: component.types,
    };
}
